import { useCallback, useEffect, useRef, useState } from 'react';
import { type BlockerFunction, useBeforeUnload, useBlocker } from 'react-router';

export interface UseUnsavedChangesGuardResult {
  bypassGuard: () => void;
  guardAction: (action: GuardedAction) => void;
  isDialogOpen: boolean;
  onStay: () => void;
  onSuccess: () => void;
}

type GuardedAction = () => void;

export const useUnsavedChangesGuard = (
  hasUnsavedChanges: boolean,
): UseUnsavedChangesGuardResult => {
  const [isActionDialogOpen, setIsActionDialogOpen] = useState(false);

  const pendingActionRef = useRef<GuardedAction | null>(null);
  const bypassNextNavigationRef = useRef(false);
  const hasUnsavedChangesRef = useRef(hasUnsavedChanges);

  useBeforeUnload(
    useCallback(
      (event: BeforeUnloadEvent) => {
        if (!hasUnsavedChanges) {
          return;
        }

        event.preventDefault();
        event.returnValue = '';
      },
      [hasUnsavedChanges],
    ),
  );

  const shouldBlock = useCallback<BlockerFunction>(() => {
    if (bypassNextNavigationRef.current) {
      bypassNextNavigationRef.current = false;
      return false;
    }

    return hasUnsavedChangesRef.current;
  }, []);
  const blocker = useBlocker(shouldBlock);

  useEffect(() => {
    hasUnsavedChangesRef.current = hasUnsavedChanges;

    if (!hasUnsavedChanges && blocker.state === 'blocked') {
      blocker.proceed();
    }
  }, [blocker, hasUnsavedChanges]);

  const onStay = useCallback(() => {
    pendingActionRef.current = null;
    setIsActionDialogOpen(false);

    if (blocker.state === 'blocked') {
      blocker.reset();
    }
  }, [blocker]);

  const onSuccess = useCallback(() => {
    const pendingAction = pendingActionRef.current;
    pendingActionRef.current = null;
    setIsActionDialogOpen(false);

    if (pendingAction) {
      bypassNextNavigationRef.current = true;

      try {
        pendingAction();
      } finally {
        bypassNextNavigationRef.current = false;
      }
    }

    if (blocker.state === 'blocked') {
      blocker.proceed();
    }
  }, [blocker]);

  const guardAction = useCallback((action: GuardedAction) => {
    if (!hasUnsavedChangesRef.current) {
      action();
      return;
    }

    pendingActionRef.current = action;
    setIsActionDialogOpen(true);
  }, []);

  const bypassGuard = useCallback(() => {
    bypassNextNavigationRef.current = true;
  }, []);

  return {
    bypassGuard,
    guardAction,
    isDialogOpen: isActionDialogOpen || blocker.state === 'blocked',
    onStay,
    onSuccess,
  };
};
